import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import { GlgTooltipIcon } from '../../../shared/components';
import RoundSelectorContainer from '../../containers/round_selector_container';
import DetailsRoundModalContainer from '../../containers/details_container/details_round_modal_container';
import { expandRoundName } from '../../utils/round_label_utils';
import ChevronIcon from '../chevron_icon';
import DetailsHoleTable from './details_hole_table';
import DetailsScroll from './details_scroll'
import PlayerProfilesContainer from '../../containers/details_container/player_profiles_container';
import { VIEW_MODES } from '../../constants';
import { componentText } from '../../utils/i18n_utils';

const AggregateDetails = ({ data, loading, error, viewMode, holeByHoleView, roundId, leagueId, isManagerSite, playerProfilesEnabled }) => {
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [showYardage, setShowYardage] = useState(false);

  const isMultiRound = data?.childAggregates?.length > 0;

  // Initial round pick only, when the scorecard's data first arrives. Not keyed
  // on the aggregate id: a background refresh after a rescore carries a new
  // id for the same players, and must not undo the round the user selected.
  useEffect(() => {
    if (!isMultiRound) return;
    const match = data.childAggregates.find((c) => String(c.roundId) === String(roundId));
    setSelectedRoundId(String(match?.roundId ?? data.childAggregates[0].roundId));
  }, [isMultiRound]);

  if (loading) return (
    <div className="lbv2__aggregate-details loading">
      <Loader type="Oval" color="#333F48" height={20} width={12} />
    </div>
  );

  if (error) return (
    <div className="lbv2__aggregate-details error">
      <div className="lbv2__error">
        <GlgTooltipIcon tooltip={error} />
        <div>{componentText('shared.error', 'error')}</div>
      </div>
    </div>
  );

  const activeData = isMultiRound
    ? data.childAggregates.find((c) => String(c.roundId) === selectedRoundId) ?? data.childAggregates[0]
    : data;

  if (!activeData?.nets?.[0]?.tee) return null;

  const availableRounds = isMultiRound
    ? data.rounds.filter((r) => data.childAggregates.some((c) => String(c.roundId) === String(r.id)))
    : null;

  // A round-by-round snapshot of the first round carries exactly one child
  // aggregate (its own round), so having children does not mean there is a
  // round to switch to - the picker only makes sense with two or more.
  const showRoundSelector = isMultiRound && availableRounds.length > 1;

  const roundShortName = isMultiRound
    ? availableRounds.find((r) => String(r.id) === selectedRoundId)?.name ?? 'R1'
    : 'R1';
  const roundLabel = viewMode === VIEW_MODES.WEB ? expandRoundName(roundShortName) : roundShortName;

  return (
    <div className="lbv2__aggregate-details">
      {playerProfilesEnabled && (
        <PlayerProfilesContainer memberIds={data?.memberIdsStr} memberCards={data?.memberCards ?? []} leagueId={leagueId} viewMode={viewMode} isManagerSite={isManagerSite} />
      )}
      <div className="lbv2__details-selectors">
        <div className="lbv2__left-selector">
          {showRoundSelector && viewMode === VIEW_MODES.WEB && (
            <RoundSelectorContainer
              rounds={availableRounds}
              selectedRoundId={selectedRoundId}
              onRoundChange={setSelectedRoundId}
              formatLabel={(r) => expandRoundName(r.name)}
              showPlayAt
            />
          )}
          {showRoundSelector && viewMode !== VIEW_MODES.WEB && (
            <DetailsRoundModalContainer
              rounds={availableRounds}
              selectedRoundId={selectedRoundId}
              onRoundChange={setSelectedRoundId}
            />
          )}
        </div>
        <div className="lbv2__right-selector">
          <div></div>
          <button className="lbv2__yardage-toggle-btn" onClick={() => setShowYardage((prev) => !prev)}>
            <div className="lbv2__course-name">{activeData.nets[0].tee.courseName}</div>
            <ChevronIcon isOpen={showYardage} />
          </button>
        </div>
      </div>
      <DetailsScroll>
        <DetailsHoleTable
          className="lbv2__details-table"
          data={activeData}
          viewMode={viewMode}
          holeByHoleView={holeByHoleView}
          label={roundLabel}
          showYardage={showYardage}
          onToggleYardage={() => setShowYardage((prev) => !prev)}
        />
      </DetailsScroll>
    </div>
  );
};

AggregateDetails.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  holeByHoleView: PropTypes.string,
  roundId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isManagerSite: PropTypes.bool,
  playerProfilesEnabled: PropTypes.bool,
};

export default AggregateDetails;
